(function () {
  if (window.customElements.get("read-more")) return;

  const READ_MORE_MS = 500;

  /**
   * Collapsible rich text with overflow detection (line-clamp is unreliable for block-level RTE children).
   */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector("[data-read-more-body]");
      this.footerBtn = this.querySelector("[data-read-more-footer]");
      this.headerBtn = this.querySelector("[data-read-more-header]");
      this.labelMore = this.querySelector("[data-read-more-more]");
      this.labelLess = this.querySelector("[data-read-more-less]");

      this.linesMobile = Number(this.dataset.linesMobile) || 5;
      this.linesDesktop = Number(this.dataset.linesDesktop) || 6;

      this._animGen = 0;
      this._animatingBody = false;
      this._transitionFallbackTimer = 0;

      if (!this.body) return;

      this.allowCollapse = this.getAttribute("data-allow-collapse") !== "false";
      this._mq = window.matchMedia("(min-width: 768px)");
      const capEarly = this.getCollapsedMaxPx();
      const fullEarly = this.measureFullHeight();
      const needsToggleEarly = fullEarly > capEarly + 2;
      if (!needsToggleEarly) {
        this.classList.add("read-more--no-clamp");
        this.expanded = true;
        this.setAttribute("data-expanded", "true");
      } else {
        this.expanded = this.getAttribute("data-expanded") === "true";
      }

      this.body.style.transition = `max-height ${READ_MORE_MS}ms ease`;

      this._onResize = () => {
        this._interruptBodyAnimationIfNeeded();
        this.scheduleUpdate();
      };
      this._onMq = () => {
        this._interruptBodyAnimationIfNeeded();
        this.scheduleUpdate();
      };

      window.addEventListener("resize", this._onResize);
      this._mq.addEventListener("change", this._onMq);
      this._ro = new ResizeObserver(() => this.scheduleUpdate());
      this._ro.observe(this.body);

      this._onHeaderClick = () => this.toggle();
      this._onFooterClick = () => this.toggle();

      if (this.headerBtn) {
        this.headerBtn.addEventListener("click", this._onHeaderClick);
      }
      if (this.footerBtn) {
        this.footerBtn.addEventListener("click", this._onFooterClick);
      }

      this.setExpanded(this.expanded, false);
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this._onResize);
      this._mq.removeEventListener("change", this._onMq);
      if (this._ro) this._ro.disconnect();
      if (this._transitionFallbackTimer) {
        clearTimeout(this._transitionFallbackTimer);
        this._transitionFallbackTimer = 0;
      }
      if (this.headerBtn) {
        this.headerBtn.removeEventListener("click", this._onHeaderClick);
      }
      if (this.footerBtn) {
        this.footerBtn.removeEventListener("click", this._onFooterClick);
      }
    }

    _prefersReducedMotion() {
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    }

    _interruptBodyAnimationIfNeeded() {
      if (!this._animatingBody) return;
      this._animGen++;
      this._animatingBody = false;
      this._clearTransitionFallback();
    }

    scheduleUpdate() {
      if (this._scheduled) return;
      this._scheduled = true;
      requestAnimationFrame(() => {
        this._scheduled = false;
        this.updateOverflowState();
      });
    }

    measureLineHeight(el) {
      const lh = parseFloat(window.getComputedStyle(el).lineHeight);
      if (!Number.isNaN(lh) && lh > 0) return lh;
      const probe = document.createElement("span");
      probe.textContent = "M";
      probe.setAttribute(
        "style",
        "position:absolute;left:-9999px;visibility:hidden;display:inline-block;padding:0;margin:0;font:inherit;",
      );
      el.appendChild(probe);
      const h = probe.getBoundingClientRect().height;
      el.removeChild(probe);
      return h || 16;
    }

    getCollapsedMaxPx() {
      const lines = this._mq.matches ? this.linesDesktop : this.linesMobile;
      const lh = this.measureLineHeight(this.body);
      // Add a small buffer to absorb descenders (g/p/y), sub-pixel rounding, and
      // rich-text line/paragraph spacing so the last visible line isn't clipped.
      const buffer = Math.ceil(lh * 0.35);
      return Math.ceil(lh * lines + buffer);
    }

    measureFullHeight() {
      const prevMax = this.body.style.maxHeight;
      const prevOverflow = this.body.style.overflow;
      this.body.style.maxHeight = "none";
      this.body.style.overflow = "visible";
      const h = this.body.scrollHeight;
      this.body.style.maxHeight = prevMax;
      this.body.style.overflow = prevOverflow;
      return h;
    }

    /** Apply body height/clipping without transitions (resize, mount, reconcile). */
    _applyBodyLayoutInstant(isCollapsed) {
      const cap = this.getCollapsedMaxPx();
      const prevTransition = this.body.style.transition;
      this.body.style.transition = "none";
      if (isCollapsed) {
        this.body.style.maxHeight = `${cap}px`;
        this.body.style.overflow = "hidden";
      } else {
        this.body.style.maxHeight = "";
        this.body.style.overflow = "";
      }
      void this.body.offsetHeight;
      this.body.style.transition = prevTransition;
    }

    updateOverflowState() {
      if (!this.body) return;
      if (this._animatingBody) {
        this._queueAccordionPanelSync();
        return;
      }

      const fullHeight = this.measureFullHeight();
      const cap = this.getCollapsedMaxPx();
      const needsToggle = fullHeight > cap + 2;

      if (!needsToggle) {
        this.classList.add("read-more--no-clamp");
        if (this.headerBtn) {
          this.headerBtn.hidden = true;
          this.headerBtn.setAttribute("aria-hidden", "true");
        }
        if (this.footerBtn) {
          this.footerBtn.hidden = true;
          this.footerBtn.setAttribute("aria-hidden", "true");
        }
        this.setExpanded(true, false);
        this._queueAccordionPanelSync();
        return;
      }

      this.classList.remove("read-more--no-clamp");

      const showControls = !this.expanded || this.allowCollapse;
      if (this.headerBtn) {
        this.headerBtn.hidden = !showControls;
        this.headerBtn.setAttribute("aria-hidden", showControls ? "false" : "true");
      }
      if (this.footerBtn) {
        this.footerBtn.hidden = !showControls;
        this.footerBtn.setAttribute("aria-hidden", showControls ? "false" : "true");
      }

      if (!this.expanded) {
        this._applyBodyLayoutInstant(true);
      } else {
        this._applyBodyLayoutInstant(false);
      }

      this._syncLabels();
      this._queueAccordionPanelSync();
    }

    /**
     * accordion-js pins `.ac-panel` to pixel height while open; refresh when inner content height changes.
     */
    _queueAccordionPanelSync() {
      if (this._accordionSyncQueued) return;
      this._accordionSyncQueued = true;
      requestAnimationFrame(() => {
        this._accordionSyncQueued = false;
        this._syncAccordionPanel();
      });
    }

    _syncAccordionPanel() {
      const ac = this.closest(".ac");
      if (!ac || !ac.classList.contains("is-active")) return;
      const panel = ac.querySelector(".ac-panel");
      if (!panel) return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const current = panel.getBoundingClientRect().height;
          const target = panel.scrollHeight;
          if (!Number.isFinite(current) || !Number.isFinite(target)) return;
          if (Math.abs(current - target) < 3) return;

          const currentPx = `${Math.round(Math.max(current, 1))}px`;
          const targetPx = `${Math.round(Math.max(target, 0))}px`;

          panel.style.height = currentPx;
          void panel.offsetHeight;
          panel.style.height = targetPx;
        });
      });
    }

    _clearTransitionFallback() {
      if (this._transitionFallbackTimer) {
        clearTimeout(this._transitionFallbackTimer);
        this._transitionFallbackTimer = 0;
      }
    }

    _scheduleTransitionFallback(onComplete) {
      this._clearTransitionFallback();
      this._transitionFallbackTimer = window.setTimeout(() => {
        this._transitionFallbackTimer = 0;
        onComplete();
      }, READ_MORE_MS + 80);
    }

    _finishBodyAnimation(gen) {
      return () => {
        if (gen !== this._animGen) return;
        this._clearTransitionFallback();
        this._animatingBody = false;
        this.scheduleUpdate();
        this._queueAccordionPanelSync();
      };
    }

    _animateExpand(cap) {
      const fullH = Math.max(this.body.scrollHeight, cap + 1);
      if (this._prefersReducedMotion() || Math.abs(fullH - cap) < 3) {
        this.setExpanded(true, true);
        return;
      }

      const gen = ++this._animGen;
      this._animatingBody = true;
      this.expanded = true;
      this.setAttribute("data-expanded", "true");
      this._syncAriaExpanded(true);

      this.body.style.overflow = "hidden";
      this.body.style.maxHeight = `${cap}px`;
      void this.body.offsetHeight;
      this.body.style.maxHeight = `${fullH}px`;

      this._syncLabels();

      const finish = this._finishBodyAnimation(gen);
      const onEnd = (e) => {
        if (e.target !== this.body || e.propertyName !== "max-height") return;
        this.body.removeEventListener("transitionend", onEnd);
        if (gen !== this._animGen) return;
        this.body.style.maxHeight = "";
        this.body.style.overflow = "";
        this._clearTransitionFallback();
        finish();
      };
      this.body.addEventListener("transitionend", onEnd);
      this._scheduleTransitionFallback(() => {
        this.body.removeEventListener("transitionend", onEnd);
        if (gen !== this._animGen) return;
        this.body.style.maxHeight = "";
        this.body.style.overflow = "";
        finish();
      });
    }

    _animateCollapse(cap) {
      const fullH = Math.max(this.body.scrollHeight, cap + 1);
      if (this._prefersReducedMotion() || Math.abs(fullH - cap) < 3) {
        this.setExpanded(false, true);
        return;
      }

      const gen = ++this._animGen;
      this._animatingBody = true;
      this.expanded = false;
      this.setAttribute("data-expanded", "false");
      this._syncAriaExpanded(false);

      this.body.style.overflow = "hidden";
      this.body.style.maxHeight = `${fullH}px`;
      void this.body.offsetHeight;
      this.body.style.maxHeight = `${cap}px`;

      this._syncLabels();

      const finish = this._finishBodyAnimation(gen);
      const onEnd = (e) => {
        if (e.target !== this.body || e.propertyName !== "max-height") return;
        this.body.removeEventListener("transitionend", onEnd);
        if (gen !== this._animGen) return;
        this.body.style.maxHeight = `${cap}px`;
        this.body.style.overflow = "hidden";
        this._clearTransitionFallback();
        finish();
      };
      this.body.addEventListener("transitionend", onEnd);
      this._scheduleTransitionFallback(() => {
        this.body.removeEventListener("transitionend", onEnd);
        if (gen !== this._animGen) return;
        this.body.style.maxHeight = `${cap}px`;
        this.body.style.overflow = "hidden";
        finish();
      });
    }

    _syncAriaExpanded(open) {
      if (this.headerBtn) {
        this.headerBtn.setAttribute("aria-expanded", open ? "true" : "false");
      }
      if (this.footerBtn) {
        this.footerBtn.setAttribute("aria-expanded", open ? "true" : "false");
      }
    }

    setExpanded(next, updateOverflow = true) {
      this.expanded = next;
      this.setAttribute("data-expanded", next ? "true" : "false");

      if (!this.body) return;

      if (this._animatingBody) {
        this._animGen++;
        this._animatingBody = false;
        this._clearTransitionFallback();
      }

      const cap = this.getCollapsedMaxPx();
      this._applyBodyLayoutInstant(!next);

      this._syncAriaExpanded(next);

      this._syncLabels();

      if (updateOverflow) this.scheduleUpdate();
    }

    _syncLabels() {
      if (!this.labelMore || !this.labelLess) return;
      const showLess = this.expanded && this.allowCollapse;
      this.labelMore.classList.toggle("hidden", showLess);
      this.labelLess.classList.toggle("hidden", !showLess);
    }

    toggle() {
      if (!this.body) return;

      const fullHeight = this.measureFullHeight();
      const cap = this.getCollapsedMaxPx();
      const needsToggle = fullHeight > cap + 2;
      if (!needsToggle) return;

      if (this.expanded) {
        if (this.allowCollapse) {
          this._animateCollapse(cap);
        }
      } else {
        this._animateExpand(cap);
      }
    }
  }

  window.customElements.define("read-more", ReadMore);
})();
